// This checks URL syntax/schemes only. The preview/save service owns provider
// recognition and content policy; unfamiliar websites remain ordinary links.
export function normalizeExternalUrl(value) {
  if (typeof value !== 'string') return null;
  const text = value.trim();
  if (!text || /\s/.test(text) || Array.from(text).some(char => char.charCodeAt(0) < 32 || char.charCodeAt(0) === 127)) return null;
  try {
    const url = new URL(/^www\./i.test(text) ? `https://${text}` : text);
    return ['http:', 'https:'].includes(url.protocol) && url.hostname ? url.href : null;
  } catch { return null; }
}

function trimPunctuation(value) {
  let text = value.replace(/[.,!?;:]+$/, '');
  const pairs = { ')': '(', ']': '[', '}': '{' };
  while (pairs[text.at(-1)]) {
    const close = text.at(-1);
    const open = pairs[close];
    if (text.split(close).length <= text.split(open).length) break;
    text = text.slice(0, -1).replace(/[.,!?;:]+$/, '');
  }
  return text;
}

export function externalLinkSegments(value) {
  const text = String(value ?? '');
  const pattern = /(?:https?:\/\/|www\.)[^\s<>"'`«»“”]+/gi;
  const segments = [];
  let end = 0;
  for (const match of text.matchAll(pattern)) {
    // Avoid extracting a link out of an email or a larger word/token.
    if (match.index && /[\p{L}\p{N}_@/]/u.test(text[match.index - 1])) continue;
    const label = trimPunctuation(match[0]);
    const href = normalizeExternalUrl(label);
    if (!href) continue;
    if (match.index > end) segments.push({ text: text.slice(end, match.index) });
    segments.push({ text: label, href });
    end = match.index + label.length;
  }
  if (end < text.length || !segments.length) segments.push({ text: text.slice(end) });
  return segments;
}

// Upgrade old saved/draft plain links before Tiptap parses the document. This
// runs only at restoration boundaries, never on live input/IME transactions.
export function linkifyExternalHTML(value) {
  const html = String(value ?? '');
  if (!/(?:https?:\/\/|www\.)/i.test(html)) return html;
  const container = document.createElement('div');
  container.innerHTML = html;
  const walker = document.createTreeWalker(container, NodeFilter.SHOW_TEXT);
  const texts = [];
  while (walker.nextNode()) texts.push(walker.currentNode);
  let changed = false;
  texts.forEach(node => {
    if (node.parentElement?.closest('a,pre,code,script,style,textarea')) return;
    const segments = externalLinkSegments(node.textContent);
    if (!segments.some(segment => segment.href)) return;
    const fragment = document.createDocumentFragment();
    segments.forEach(segment => {
      if (!segment.href) fragment.appendChild(document.createTextNode(segment.text));
      else {
        const anchor = document.createElement('a');
        anchor.setAttribute('href', segment.href);
        anchor.textContent = segment.text;
        fragment.appendChild(anchor);
      }
    });
    node.replaceWith(fragment);
    changed = true;
  });
  return changed ? container.innerHTML : html;
}
